import api from './api';
import logger from '../utils/logger';

export const authService = {
  // Cross-device UI state (first-login tour, getting-started card)
  getUxFlags: async () => api.get('/v1/auth/ux-flags/'),
  setUxFlag: async (flag) => api.patch('/v1/auth/ux-flags/', { [flag]: true }),

  // New-member checklist, computed server-side from real activity
  getGettingStarted: async () => api.get('/v1/auth/getting-started/'),

  // Register new user
  // The register form is gated by the Terms & Privacy consent modal; the
  // server records that acceptance (GDPR Art. 7(1)) and refuses without it.
  register: async (email, password, displayName, isHcw = false, { acceptedTesting = false } = {}) => {
    const response = await api.post('/v1/auth/register/', {
      email,
      password,
      password2: password,
      display_name: displayName,
      is_hcw: isHcw,
      accepted_terms: true,
      accepted_testing_agreement: Boolean(acceptedTesting),
    });
    return response;
  },

  // Login
  login: async (email, password) => {
    const response = await api.post('/v1/auth/login/', {
      email,
      password
    });
    return response;
  },

  // Logout
  logout: async (refreshToken) => {
    try {
      await api.post('/v1/auth/logout/', { refresh: refreshToken });
    } catch (error) {
      logger.error('Logout error:', error);
    }
  },

  // Refresh token
  refreshToken: async (refreshToken) => {
    const response = await api.post('/v1/auth/token/refresh/', {
      refresh: refreshToken
    });
    return response;
  },

  // Get user profile
  getProfile: async () => {
    const response = await api.get('/v1/auth/profile/');
    return response;
  },

  // Update user profile
  updateProfile: async (data) => {
    const response = await api.patch('/v1/auth/profile/', data);
    return response;
  },

  // Verify email with 6-digit code
  verifyEmail: async (code) => {
    const response = await api.post('/v1/auth/verify-email/', { code });
    return response;
  },

  // Resend verification email
  resendVerification: async () => {
    const response = await api.post('/v1/auth/resend-verification/');
    return response;
  },

  // Request password reset (forgot password)
  forgotPassword: async (email) => {
    const response = await api.post('/v1/auth/forgot-password/', { email });
    return response;
  },

  // Reset password with token
  resetPassword: async (token, newPassword) => {
    const response = await api.post('/v1/auth/reset-password/', {
      token,
      new_password: newPassword,
      new_password2: newPassword
    });
    // Changing the password signs out every other session; keep this one on
    // the fresh tokens the server returns.
    if (response.data?.tokens?.access) {
      localStorage.setItem('authTokens', JSON.stringify(response.data.tokens));
    }
    return response;
  },

  // Request password change code (for logged in users)
  requestPasswordChangeCode: async () => {
    const response = await api.post('/v1/auth/request-password-change-code/');
    return response;
  },

  // Change password with verification code
  changePassword: async (verificationCode, oldPassword, newPassword) => {
    const response = await api.post('/v1/auth/change-password/', {
      verification_code: verificationCode,
      old_password: oldPassword,
      new_password: newPassword,
      new_password2: newPassword
    });
    return response;
  },

  // Delete account
  deleteAccount: async (password) => {
    const response = await api.post('/v1/auth/delete-account/', { password });
    return response;
  },

  // Google OAuth - exchange Google token for JWT
  googleAuth: async (accessToken, idToken = null) => {
    const response = await api.post('/v1/auth/google/', {
      access_token: accessToken,
      id_token: idToken
    });
    return response;
  },

  // Request account restoration code (for deleted accounts)
  requestRestoreCode: async (email) => {
    const response = await api.post('/v1/auth/request-restore/', { email });
    return response;
  },

  // Restore account with verification code
  restoreAccount: async (email, code) => {
    const response = await api.post('/v1/auth/restore-account/', {
      email,
      code
    });
    return response;
  }
};
